import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import CharterBundleCalendarModal from './CharterBundleCalendarModal'
import { FlightsClassTypes } from 'constants/flight'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { INTL_DAY_MONTH_NAME_SHORT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useNextAvailableCharterPeriod } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useNextAvailableCharterPeriod'
import { useNextValidCharterPeriod } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useNextValidCharterPeriod'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  nights: number
  fareClass: FlightsClassTypes
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  checkInDate?: string
  charterBundleRoute?: App.CharterBundleRoute
  rooms?: Array<App.Occupants>
  className?: string
}

/**
 * Charter Flight Calendar Select for Desktop
 * Uses ModalContext pattern to open the calendar modal
 */
function CharterFlightCalendarSelect(props: Props) {
  const {
    selectedMonth,
    onMonthChange,
    nights,
    fareClass,
    onDurationChange,
    durationOptions,
    checkInDate,
    charterBundleRoute,
    rooms,
    className,
  } = props

  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const offerPageState = useContext(OfferPageStateContext)
  const intlDateFormatter = useIntlDateFormatter()

  const handleFareClassChange = useCallback((selectedClass: FlightsClassTypes) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
  }, [dispatch])

  // Get next available charter period (checks flight and offer availability)
  // Only call hook if charterBundleRoute is available
  const nextAvailablePeriod = useNextAvailableCharterPeriod({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    rooms: rooms ?? [],
    fareClass,
    priceMode: offerPageState.priceMode ?? 'perPerson',
  })

  // Get next valid charter period (calculates valid Tuesday dates)
  const nextValidPeriod = useNextValidCharterPeriod({
    checkIn: checkInDate,
    checkOut: offerPageState.checkOutDate,
  })

  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if a date is valid for charter flights (must be Tuesday and not before start date)
  const isDateValid = useCallback((date: string | undefined): boolean => {
    if (!date) return false
    const dateMoment = moment(date).startOf('day')
    const startDate = moment(MALDIVES_CHARTER_START_TARGET_DATE).startOf('day')
    // Must be Tuesday (isoWeekday === 2) and not before start date
    return dateMoment.isoWeekday() === 2 && !dateMoment.isBefore(startDate)
  }, [])

  // Set default dates if checkInDate is not set, or correct invalid dates
  useEffect(() => {
    if (!checkInDate) {
      // Use next available period if available, otherwise fall back to calculated dates
      // Check-in is departure date + 1 (arrive next day), check-out is check-in + nights
      const defaultCheckIn = nextAvailablePeriod?.checkIn ?? moment(MALDIVES_CHARTER_START_TARGET_DATE).add(1, 'day').format(ISO_DATE_FORMAT)
      const defaultCheckOut = nextAvailablePeriod?.checkOut ?? moment(defaultCheckIn).add(MALDIVES_BUNDLE_DEFAULT_NIGHTS, 'days').format(ISO_DATE_FORMAT)

      dispatch({
        type: OfferPageActions.setDates,
        checkIn: defaultCheckIn,
        checkOut: defaultCheckOut,
      })
    } else if (!isDateValid(checkInDate)) {
      // If checkInDate is invalid (not Tuesday or before start date), use next valid period
      if (nextValidPeriod.fallbackCheckinDate && nextValidPeriod.effectiveReturnDate) {
        dispatch({
          type: OfferPageActions.setDates,
          checkIn: nextValidPeriod.fallbackCheckinDate,
          checkOut: nextValidPeriod.effectiveReturnDate,
        })
      }
    }
  }, [checkInDate, dispatch, nextAvailablePeriod, nextValidPeriod, isDateValid])

  const openCalendarModal = useCallback(async() => {
    const selectedDates = await openModal<{ departDate: string, returnDate: string, checkIn: string, checkOut: string } | undefined>(
      <CharterBundleCalendarModal
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        onDurationChange={onDurationChange}
        durationOptions={durationOptions}
        onFareClassChange={handleFareClassChange}
      />
    )

    if (selectedDates) {
      // Update dates in offer page state
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut,
      })
      dispatch({ type: OfferPageActions.closeFilter })
    }
  }, [
    openModal,
    dispatch,
    selectedMonth,
    onMonthChange,
    onDurationChange,
    durationOptions,
    handleFareClassChange,
  ])

  // Display selected date or placeholder
  // For charter bundle, display flight dates (departDate/returnDate) instead of hotel dates (checkIn/checkOut)
  const value = useMemo(() => {
    // Use flight dates if available (charter bundle mode), otherwise fall back to hotel dates
    if (departDate && returnDate) {
      const dateText = `${intlDateFormatter(departDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(returnDate, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> {nightsText}</>
    }
    if (checkInDate) {
      const checkOut = moment(checkInDate).add(nights, 'days').format('YYYY-MM-DD')
      const dateText = `${intlDateFormatter(checkInDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(checkOut, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> {nightsText}</>
    }
    return undefined
  }, [checkInDate, departDate, returnDate, nights, intlDateFormatter])

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="charter-calendar-select"
      onClick={openCalendarModal}
      icon={<LineCalendarIcon />}
      label="When"
      value={value}
      placeholder="Select your dates"
    />
  )
}

export default CharterFlightCalendarSelect
